
import { useCallback, useMemo } from 'react';
import { ContentType } from '@/services/comments/types';
import { useCollectionRealtime } from '../realtime/useCollectionRealtime';

interface UseCommentVotesRealtimeProps {
  contentType: ContentType;
  commentIds: string[];
  onVotesChange?: (commentId: string, voteCount: number, upvoteCount: number, downvoteCount: number) => void;
}

/**
 * Hook for subscribing to real-time comment vote updates using Universal Real-time Manager
 */
export const useCommentVotesRealtime = ({
  contentType,
  commentIds,
  onVotesChange
}: UseCommentVotesRealtimeProps) => {
  
  // Memoize filters to prevent unnecessary re-subscriptions
  const filters = useMemo(() => {
    // For comment votes, we don't need specific filters as we'll handle filtering in the event handlers
    return {};
  }, []);

  // Handle vote count updates from realtime events
  const handleVoteUpdate = useCallback(async (payload: any) => {
    if (!onVotesChange) return;
    
    const vote = payload;
    if (!vote?.comment_id || !commentIds.includes(vote.comment_id)) {
      return;
    }

    console.log('[Universal Realtime] Processing vote update for comment:', vote.comment_id);
    
    // For now, we'll need to refetch counts as we don't have aggregated data in the payload
    // This is a simplified approach that maintains functionality
    try {
      // Import the vote service to get updated counts
      const { getCommentVoteCount } = await import('@/services/commentVoteService');
      
      const [totalCount, upCount, downCount] = await Promise.all([
        getCommentVoteCount(vote.comment_id, contentType),
        getCommentVoteCount(vote.comment_id, contentType, 'up'),
        getCommentVoteCount(vote.comment_id, contentType, 'down')
      ]);
      
      onVotesChange(vote.comment_id, totalCount, upCount, downCount);
    } catch (error) {
      console.error('[Universal Realtime] Error fetching updated vote counts:', error);
    }
  }, [commentIds, contentType, onVotesChange]);

  // Use Universal Real-time Manager for comment votes
  const { isConnected, connectionStatus, lastEvent, lastEventTime } = useCollectionRealtime(
    'comments', // This will be extended to handle vote tables
    filters,
    {
      onItemAdded: handleVoteUpdate,
      onItemUpdated: handleVoteUpdate,
      onItemRemoved: handleVoteUpdate,
      onError: (error) => {
        console.error('[Universal Realtime] Comment votes error:', error);
      }
    },
    commentIds.length > 0 // Only enabled when we have comment IDs to monitor
  );

  return { 
    isSubscribed: isConnected,
    lastEvent,
    lastEventTime,
    subscriptionId: connectionStatus === 'connected' ? `universal_${Date.now()}` : null,
    resubscribe: () => {
      console.log('[Universal Realtime] Resubscribe requested for comment votes');
      // The Universal Real-time Manager handles reconnection automatically
    }
  };
};
